import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit, FileText, Calendar, Clock, DollarSign, User, AlertCircle, Plus, CheckCircle2, XCircle, Pause, Mail, Phone, MapPin, Archive, FolderOpen, Trash2, GitBranch, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { usePracticeStore } from '@/store/practiceStore';
import { useAppointmentStore } from '@/store/appointmentStore';
import { useActivityStore } from '@/store/activityStore';
import { formatDate, formatDateTime, formatItalianDateTime } from '@/utils/date';
import { toast } from 'sonner';
import CreateActivityDialog from '@/components/activities/CreateActivityDialog';
import CreateAppointmentDialog from '@/components/appointments/CreateAppointmentDialog';
import EditAppointmentDialog from '@/components/appointments/EditAppointmentDialog';
import { FileExplorer } from '@/components/documents';
import { apiClient } from '@/utils/api';
import { useAuthStore } from '@/store/authStore';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { BillingPracticePanel } from '@/pages/billing/BillingPracticePanel';

export default function PracticeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('info');
  const [showCreateActivityDialog, setShowCreateActivityDialog] = useState(false);
  const [showCreateAppointmentDialog, setShowCreateAppointmentDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<{ id: string; appointment: any } | null>(null);
  const [notesValue, setNotesValue] = useState('');
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [folderRecountNonce, setFolderRecountNonce] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  /** Cambio cartella: dopo il dialog nativo Electron, window.confirm non è affidabile; usiamo AlertDialog. */
  const [pendingDocumentPathChange, setPendingDocumentPathChange] = useState<string | null>(null);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'timeline' && id) {
      fetchPractice(id);
    }
  };
  
  const {
    currentPractice: practice,
    loading,
    error,
    fetchPractice,
    updatePractice,
    deletePractice,
    archivePractice,
    clearError,
  } = usePracticeStore();

  const { user } = useAuthStore();

  const {
    appointments,
    fetchAppointments,
  } = useAppointmentStore();

  const {
    fetchActivitiesByPractice,
  } = useActivityStore();

  const [practiceActivities, setPracticeActivities] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      fetchPractice(id).catch(() => {
        toast.error('Errore nel caricamento della pratica');
      });
      fetchAppointments({ practiceId: id }).catch(() => {
        console.error('Errore nel caricamento appuntamenti');
      });
      fetchActivitiesByPractice(id).then((activities) => {
        setPracticeActivities(activities);
      }).catch(() => {
        console.error('Errore nel caricamento attività');
      });
    }
  }, [id, fetchPractice, fetchAppointments, fetchActivitiesByPractice]);

  // Aggiorna notesValue quando cambia la pratica
  useEffect(() => {
    if (practice) {
      setNotesValue(practice.notes || '');
    }
  }, [practice]);

  // Gestisci apertura dialog modifica appuntamento quando si naviga dal calendario
  useEffect(() => {
    if (location.state && (location.state as any).editAppointmentId && practice && appointments.length > 0) {
      const appointmentId = (location.state as any).editAppointmentId;
      const appointment = appointments.find(apt => apt.id === appointmentId);
      if (appointment) {
        setEditingAppointment({ id: appointment.id, appointment });
        setActiveTab('appointments');
        // Pulisci lo state per evitare ri-aperture
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, appointments, practice]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (
      tab === 'info' ||
      tab === 'activities' ||
      tab === 'appointments' ||
      tab === 'documents' ||
      tab === 'timeline' ||
      tab === 'notes'
    ) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (practice?.folderFileCount != null && practice.folderFileCount >= 0) {
      setDocumentCount(practice.folderFileCount);
    }
  }, [practice?.id, practice?.folderFileCount]);

  const getStatusBadge = (status: string) => {
    const config: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', icon: any }> = {
      PENDING: { variant: 'secondary', icon: Clock },
      IN_PROGRESS: { variant: 'default', icon: Clock },
      SUSPENDED: { variant: 'outline', icon: Pause },
      COMPLETED: { variant: 'default', icon: CheckCircle2 },
      ARCHIVED: { variant: 'outline', icon: XCircle },
    };
    const cfg = config[status] || { variant: 'secondary' as const, icon: FileText };
    const Icon = cfg.icon;
    return (
      <Badge variant={cfg.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      LOW: 'bg-gray-100 text-gray-800',
      MEDIUM: 'bg-blue-100 text-blue-800',
      HIGH: 'bg-orange-100 text-orange-800',
      URGENT: 'bg-red-100 text-red-800',
    };
    return (
      <Badge className={colors[priority] || 'bg-gray-100 text-gray-800'}>
        {priority}
      </Badge>
    );
  };

  if (loading && !practice) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/practices">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna alle pratiche
            </Button>
          </Link>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!practice) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/practices">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna alle pratiche
            </Button>
          </Link>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">Pratica non trovata</h3>
              <p className="mt-1 text-sm text-gray-500">
                La pratica richiesta non esiste o non hai i permessi per visualizzarla.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const practiceAppointments = appointments.filter(apt => apt.practiceId === practice.id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={practice.parentId ? `/practices/${practice.parentId}` : '/practices'}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {practice.parentId ? 'Torna alla pratica padre' : 'Torna alle pratiche'}
            </Button>
          </Link>
          <div>
            {practice.parent && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                <Link to="/practices" className="hover:underline">Pratiche</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to={`/practices/${practice.parent.id}`} className="hover:underline">
                  {practice.parent.practiceNumber ? `#${practice.parent.practiceNumber} ` : ''}{practice.parent.title}
                </Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-foreground font-medium">Sotto-pratica</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              {practice.practiceNumber && (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-sm font-mono rounded">
                  #{practice.practiceNumber}
                </span>
              )}
              <h1 className="text-2xl font-bold text-gray-900">{practice.title}</h1>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {practice.parentId ? 'Sotto-pratica creata' : 'Pratica creata'} il {formatDate(practice.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {practice.status === 'COMPLETED' && (
            <Button
              variant="outline"
              onClick={async () => {
                if (id && window.confirm('Sei sicuro di voler archiviare questa pratica?')) {
                  try {
                    await archivePractice(id);
                    toast.success('Pratica archiviata con successo');
                    if (id) {
                      fetchPractice(id);
                    }
                  } catch (error) {
                    toast.error('Errore nell\'archiviazione della pratica');
                  }
                }
              }}
            >
              <Archive className="h-4 w-4 mr-2" />
              Archivia
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/practices/${id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifica
          </Button>

          {user?.role === 'ADMIN' && (
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteConfirmText('');
                setShowDeleteDialog(true);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Elimina
            </Button>
          )}
        </div>
      </div>

      {/* Eliminazione definitiva (solo ADMIN) */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente la pratica?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è irreversibile. Per confermare, inserisci{' '}
              {practice.practiceNumber ? 'il numero pratica' : "l'ID pratica"} esatto.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Valore richiesto:{' '}
              <span className="font-mono text-foreground">
                {practice.practiceNumber ?? practice.id}
              </span>
            </div>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={practice.practiceNumber ? 'Inserisci numero pratica' : "Inserisci l'ID pratica"}
              autoFocus
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting || deleteConfirmText !== (practice.practiceNumber ?? practice.id) || !id}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async (e) => {
                e.preventDefault();
                if (!id) return;
                try {
                  setIsDeleting(true);
                  await deletePractice(id, deleteConfirmText);
                  toast.success('Pratica eliminata definitivamente');
                  setShowDeleteDialog(false);
                  navigate('/practices');
                } catch (err: any) {
                  toast.error(err?.response?.data?.message || "Errore nell'eliminazione della pratica");
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingDocumentPathChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDocumentPathChange(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modificare il percorso documenti?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione cambierà il collegamento alla cartella della pratica.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                const path = pendingDocumentPathChange;
                if (!path || !practice?.id) return;
                try {
                  await apiClient.patch(`/practices/${practice.id}/document-path`, {
                    documentPath: path,
                  });
                  toast.success('Percorso documenti aggiornato');
                  setPendingDocumentPathChange(null);
                  if (id) fetchPractice(id);
                } catch {
                  toast.error('Errore nel salvataggio del percorso documenti');
                }
              }}
            >
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status and Priority */}
      <div className="flex items-center gap-4">
        {getStatusBadge(practice.status)}
        {getPriorityBadge(practice.priority)}
        {practice.type && (
          <Badge variant="outline" className="flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {practice.type.name}
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="info">Info</TabsTrigger>
          <TabsTrigger value="activities">
            Attività {practiceActivities && practiceActivities.length > 0 && `(${practiceActivities.length})`}
          </TabsTrigger>
          <TabsTrigger value="appointments">
            Appuntamenti {practiceAppointments.length > 0 && `(${practiceAppointments.length})`}
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FolderOpen className="h-4 w-4 mr-1" />
            Documenti
          </TabsTrigger>
          <TabsTrigger value="sub-practices">
            <GitBranch className="h-4 w-4 mr-1" />
            Sotto-pratiche {practice.children && practice.children.length > 0 && `(${practice.children.length})`}
          </TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="notes">Note</TabsTrigger>
        </TabsList>

        {/* Tab Info */}
        <TabsContent value="info" className="space-y-4 mt-6">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-700">Informazioni Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">
                      {practice.client?.firstName} {practice.client?.lastName}
                    </span>
                  </div>
                  {practice.client?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{practice.client.email}</span>
                    </div>
                  )}
                  {practice.client?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{practice.client.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-700">Scadenze</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">
                      Inizio: {formatDate(practice.startDate)}
                    </span>
                  </div>
                  {practice.dueDate && (() => {
                    const isClosedPractice = practice.status === 'COMPLETED' || practice.status === 'ARCHIVED';
                    const isPastDue = new Date(practice.dueDate) < new Date();
                    const iconColor = isClosedPractice ? 'text-gray-400' : (isPastDue ? 'text-red-500' : 'text-orange-500');
                    
                    let statusText = '';
                    if (isClosedPractice && practice.completedAt) {
                      const completedDate = new Date(practice.completedAt);
                      const dueDate = new Date(practice.dueDate);
                      if (completedDate > dueDate) {
                        const daysLate = Math.ceil((completedDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
                        statusText = ` (chiusa con ${daysLate} giorni di ritardo)`;
                      } else {
                        statusText = ' (chiusa in tempo)';
                      }
                    } else if (!isClosedPractice && isPastDue) {
                      const daysOverdue = Math.ceil((new Date().getTime() - new Date(practice.dueDate).getTime()) / (1000 * 60 * 60 * 24));
                      statusText = ` (scaduta ${daysOverdue} giorni fa)`;
                    }
                    
                    return (
                      <div className="flex items-center gap-2">
                        <AlertCircle className={`h-4 w-4 ${iconColor}`} />
                        <span className={`text-sm ${!isClosedPractice && isPastDue ? 'text-red-600 font-semibold' : ''}`}>
                          Scadenza: {formatDate(practice.dueDate)}
                          {statusText && <span className={isClosedPractice ? 'text-gray-500' : ''}>{statusText}</span>}
                        </span>
                      </div>
                    );
                  })()}
                  {practice.completedAt && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm">
                        Completata: {formatDate(practice.completedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {user?.canAccessQuotesAndAccounts && practice.amount && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-gray-700">Economico</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">
                        Importo: €{Number(practice.amount).toFixed(2)}
                      </span>
                    </div>
                    {practice.paidAmount !== undefined && practice.paidAmount !== null && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          Pagato: €{Number(practice.paidAmount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {practice.isPaid && (
                      <Badge variant="default" className="w-fit">
                        Pagata
                      </Badge>
                    )}
                    {practice.invoiceNumber && (
                      <div className="text-sm text-gray-500">
                        Fattura: {practice.invoiceNumber}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {user?.canAccessQuotesAndAccounts && (
              <BillingPracticePanel practiceId={practice.id} clientId={practice.clientId} />
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-700">Statistiche</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm">
                    Documenti: {documentCount > 0 ? documentCount : (practice.documentPath ? 'Cartella configurata' : 'Non configurato')}
                  </div>
                  <div className="text-sm">
                    Attività: {practice.activities?.length || practice._count?.activities || 0}
                  </div>
                  <div className="text-sm">
                    Appuntamenti: {practiceAppointments.length}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {practice.description && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-700">Descrizione</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {practice.description}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab Attività */}
        <TabsContent value="activities" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Attività</CardTitle>
                <Button size="sm" onClick={() => setShowCreateActivityDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Attività
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {practiceActivities && practiceActivities.length > 0 ? (
                <div className="space-y-3">
                  {practiceActivities.map((activity: any) => (
                    <div key={activity.id} className="flex items-start justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex-1">
                        <Link 
                          to={`/activities/${activity.id}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {activity.title}
                        </Link>
                        {activity.description && (
                          <div className="text-sm text-gray-600 mt-1">{activity.description}</div>
                        )}
                        <div className="text-xs text-gray-500 mt-2 flex items-center gap-4">
                          <span>{formatItalianDateTime(activity.createdAt)}</span>
                          {activity.dueDate && (
                            <span>Scadenza: {formatDate(activity.dueDate)}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={activity.status === 'COMPLETED' ? 'default' : 'secondary'}>
                          {activity.status}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/activities/${activity.id}/edit`)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Modifica
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Nessuna attività registrata</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setShowCreateActivityDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Crea prima attività
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Appuntamenti */}
        <TabsContent value="appointments" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Appuntamenti</CardTitle>
                <Button size="sm" onClick={() => setShowCreateAppointmentDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Appuntamento
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {practiceAppointments.length > 0 ? (
                <div className="space-y-3">
                  {practiceAppointments.map((apt) => (
                    <div key={apt.id} className="flex items-start justify-between p-3 border rounded-lg bg-blue-50">
                      <div className="flex-1">
                        <div className="font-medium">{apt.title}</div>
                        {apt.description && (
                          <div className="text-sm text-gray-600 mt-1">{apt.description}</div>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatItalianDateTime(apt.startTime)}
                          </span>
                          {apt.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {apt.location}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={apt.status === 'COMPLETED' ? 'default' : 'secondary'}>
                          {apt.status}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingAppointment({ id: apt.id, appointment: apt })}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Nessun appuntamento associato</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setShowCreateAppointmentDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Crea primo appuntamento
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Documenti - forceMount per evitare unmount/remount di FileExplorer */}
        <TabsContent value="documents" className="mt-6" forceMount hidden={activeTab !== 'documents'}>
          <Card className="h-[600px]">
            <CardContent className="p-0 h-full flex flex-col">
              {isElectronEnvironment() && practice.documentPath && (
                <div className="flex justify-end gap-2 p-2 border-b bg-muted/30">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFolderRecountNonce((n) => n + 1)}
                  >
                    Aggiorna conteggio (NAS)
                  </Button>
                </div>
              )}
              <FileExplorer
                practiceId={practice.id}
                basePath={practice.documentPath || undefined}
                serverFolderFileCount={practice.folderFileCount ?? null}
                recountNonce={folderRecountNonce}
                onPathConfigured={async (path) => {
                  const isChange = !!practice.documentPath;
                  if (isChange) {
                    setPendingDocumentPathChange(path);
                    return;
                  }
                  try {
                    await apiClient.patch(`/practices/${practice.id}/document-path`, {
                      documentPath: path,
                    });
                    toast.success('Percorso documenti configurato');
                    if (id) {
                      fetchPractice(id);
                    }
                  } catch {
                    toast.error('Errore nel salvataggio del percorso documenti');
                  }
                }}
                onFileCountChange={(count) => setDocumentCount(count)}
                onFolderCountPersisted={() => {
                  if (id) fetchPractice(id);
                }}
                className="h-full flex-1 min-h-0"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Sotto-pratiche */}
        <TabsContent value="sub-practices" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Sotto-pratiche</CardTitle>
                <Button
                  size="sm"
                  onClick={() => navigate(`/practices/new?parentId=${practice.id}`)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova sotto-pratica
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {practice.children && practice.children.length > 0 ? (
                <div className="space-y-3">
                  {practice.children.map((child: any) => (
                    <div
                      key={child.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/practices/${child.id}`)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-2">
                            {child.practiceNumber && (
                              <span className="text-xs font-mono text-muted-foreground">#{child.practiceNumber}</span>
                            )}
                            <span className="font-medium text-sm truncate">{child.title}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {child.type && (
                              <Badge variant="outline" className="text-xs">
                                {child.type.icon || ''} {child.type.name}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {child.status === 'PENDING' ? 'In Attesa' :
                               child.status === 'IN_PROGRESS' ? 'In Corso' :
                               child.status === 'COMPLETED' ? 'Completata' :
                               child.status === 'SUSPENDED' ? 'Sospesa' :
                               child.status === 'ARCHIVED' ? 'Archiviata' : child.status}
                            </Badge>
                            {child.assignedTo && (
                              <span className="text-xs text-muted-foreground">
                                <User className="h-3 w-3 inline mr-1" />
                                {child.assignedTo.firstName} {child.assignedTo.lastName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground shrink-0">
                        {child._count && (
                          <>
                            <span>{child._count.activities} attività</span>
                            <span>{child._count.documents} doc</span>
                            {child._count.children > 0 && (
                              <span>{child._count.children} sotto</span>
                            )}
                          </>
                        )}
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <GitBranch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna sotto-pratica</p>
                  <p className="text-xs mt-1">Crea una sotto-pratica per organizzare le attività di questa pratica</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Timeline */}
        <TabsContent value="timeline" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {practice.timeline && practice.timeline.length > 0 ? (
                <div className="space-y-4">
                  {practice.timeline.map((event: any, index: number) => {
                    const isLast = index === (practice.timeline?.length || 0) - 1;
                    const getEventColor = () => {
                      switch (event.type) {
                        case 'created': return 'bg-blue-600';
                        case 'started': return 'bg-green-600';
                        case 'activity_completed': return 'bg-purple-600';
                        case 'completed': return 'bg-green-600';
                        case 'status_changed': return 'bg-orange-600';
                        case 'appointment_created': return 'bg-blue-500';
                        case 'appointment_updated': return 'bg-indigo-600';
                        case 'updated': return 'bg-yellow-600';
                        default: return 'bg-gray-600';
                      }
                    };
                    const getEventIcon = () => {
                      switch (event.type) {
                        case 'created': return <FileText className="h-3 w-3" />;
                        case 'started': return <Clock className="h-3 w-3" />;
                        case 'activity_completed': return <CheckCircle2 className="h-3 w-3" />;
                        case 'completed': return <CheckCircle2 className="h-3 w-3" />;
                        case 'appointment_created': return <Calendar className="h-3 w-3" />;
                        case 'appointment_updated': return <Edit className="h-3 w-3" />;
                        case 'updated': return <Edit className="h-3 w-3" />;
                        default: return <AlertCircle className="h-3 w-3" />;
                      }
                    };

                    return (
                      <div key={event.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`h-4 w-4 rounded-full ${getEventColor()} flex items-center justify-center text-white`}>
                            {getEventIcon()}
                          </div>
                          {!isLast && <div className="w-0.5 h-full bg-gray-200"></div>}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="font-medium">{event.title}</div>
                          <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                          {event.userName && (
                            <div className="text-xs text-gray-500 mt-1">
                              da {event.userName}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {formatItalianDateTime(new Date(event.date))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Nessun evento nella timeline</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Note */}
        <TabsContent value="notes" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Note</CardTitle>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (practice && notesValue !== (practice.notes || '')) {
                      updatePractice(practice.id, { notes: notesValue })
                        .then(() => {
                          toast.success('Note salvate con successo');
                          // Aggiorna la pratica nel store per sincronizzare
                          fetchPractice(practice.id);
                        })
                        .catch(() => {
                          toast.error('Errore nel salvataggio delle note');
                        });
                    } else {
                      toast.info('Nessuna modifica da salvare');
                    }
                  }}
                  disabled={!practice || notesValue === (practice.notes || '')}
                >
                  Salva Note
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notesValue}
                onChange={(e) => {
                  setNotesValue(e.target.value);
                }}
                placeholder="Inserisci note sulla pratica..."
                rows={10}
                className="w-full"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Creazione Attività */}
      {practice && (
        <CreateActivityDialog
          open={showCreateActivityDialog}
          onOpenChange={setShowCreateActivityDialog}
          practiceId={practice.id}
          clientName={practice.client ? `${practice.client.firstName} ${practice.client.lastName}` : undefined}
          interventionLocation={practice.interventionLocation || undefined}
          clientAddress={!practice.interventionLocation && practice.client && 'address' in practice.client && practice.client.address ? `${practice.client.address}${('city' in practice.client && practice.client.city) ? `, ${practice.client.city}` : ''}` : undefined}
          onSuccess={() => {
            if (id) {
              fetchActivitiesByPractice(id).then((activities) => {
                setPracticeActivities(activities);
              });
              fetchPractice(id);
            }
          }}
        />
      )}

      {/* Dialog Creazione Appuntamento */}
      {practice && (
        <CreateAppointmentDialog
          open={showCreateAppointmentDialog}
          onOpenChange={setShowCreateAppointmentDialog}
          practiceId={practice.id}
          clientId={practice.clientId}
          clientName={practice.client ? `${practice.client.firstName} ${practice.client.lastName}` : undefined}
          interventionLocation={practice.interventionLocation || undefined}
          clientAddress={!practice.interventionLocation && practice.client && 'address' in practice.client && practice.client.address ? `${practice.client.address}${('city' in practice.client && practice.client.city) ? `, ${practice.client.city}` : ''}` : undefined}
          onSuccess={() => {
            if (id) {
              fetchAppointments({ practiceId: id });
            }
          }}
        />
      )}

      {/* Dialog Modifica Appuntamento */}
      {editingAppointment && (
        <EditAppointmentDialog
          open={!!editingAppointment}
          onOpenChange={(open) => {
            if (!open) {
              setEditingAppointment(null);
            }
          }}
          appointmentId={editingAppointment.id}
          appointment={editingAppointment.appointment}
          onSuccess={() => {
            if (id) {
              fetchAppointments({ practiceId: id });
              fetchPractice(id); // Ricarica pratica per aggiornare timeline
            }
            setEditingAppointment(null);
          }}
        />
      )}
    </div>
  );
}

